import { AlertCircleIcon } from 'lucide-react'
import { type ReactNode, useMemo } from 'react'

import { cn } from '@/lib/cn'
import type { ObjectWithId } from '@/types/objectWithIdTypes'
import { Alert, AlertDescription, AlertTitle } from '../alert'
import * as ShadCn from './table'

export type TableProps<TColumn extends object, TData extends object> = {
  caption?: React.ReactNode
  columns: Record<
    Exclude<keyof TColumn, 'id'>,
    {
      label: string
      dataClassName?: string
      rowFormatter?: (row: TData) => ReactNode
    }
  >
  data: ObjectWithId<TData>
  emptyDataContainer?: ReactNode
  errorMessage?: string
}

export const Table = <TColumn extends object, TData extends object = object>({
  caption,
  columns,
  data,
  emptyDataContainer,
  errorMessage,
}: TableProps<TColumn, TData>) => {
  const columnsWithoutId = useMemo(
    () => Object.keys(columns) as Exclude<keyof TColumn, 'id'>[],
    [columns],
  )

  return (
    <ShadCn.Table>
      {caption && (
        <ShadCn.TableCaption className="rounded-t-lg border border-b-0 border-system-border-primary bg-white px-4 py-2">
          {caption}
        </ShadCn.TableCaption>
      )}
      <ShadCn.TableHeader>
        <ShadCn.TableRow className="bg-system-background-primary">
          {columnsWithoutId.map((column) => (
            <ShadCn.TableHead
              key={String(column)}
              className="w-fit font-semibold text-system-text-primary"
            >
              {columns[column].label}
            </ShadCn.TableHead>
          ))}
        </ShadCn.TableRow>
      </ShadCn.TableHeader>
      <ShadCn.TableBody
        className={cn((errorMessage || !data.length) && 'bg-white')}
      >
        {errorMessage ? (
          <ShadCn.TableRow>
            <ShadCn.TableCell colSpan={columnsWithoutId.length}>
              <Alert variant="destructive">
                <AlertCircleIcon className="size-4" />
                <AlertTitle>Ops</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            </ShadCn.TableCell>
          </ShadCn.TableRow>
        ) : data.length ? (
          data.map((row, index) => (
            <ShadCn.TableRow
              key={row.id}
              className={
                index % 2 !== 0
                  ? 'bg-system-background-primary'
                  : 'bg-system-background-base'
              }
            >
              {columnsWithoutId.map((column) => {
                const columnMapper = columns[column]
                const rowValue = row[column as keyof typeof row]

                return (
                  <ShadCn.TableCell
                    key={String(column)}
                    className={cn(
                      'font-medium text-system-text-primary',
                      columnMapper.dataClassName,
                    )}
                  >
                    {columnMapper?.rowFormatter
                      ? columnMapper.rowFormatter(row)
                      : (rowValue as ReactNode)}
                  </ShadCn.TableCell>
                )
              })}
            </ShadCn.TableRow>
          ))
        ) : (
          <ShadCn.TableRow>
            <ShadCn.TableCell colSpan={columnsWithoutId.length}>
              {emptyDataContainer}
            </ShadCn.TableCell>
          </ShadCn.TableRow>
        )}
      </ShadCn.TableBody>
    </ShadCn.Table>
  )
}
